import React, { useContext, useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Box,
  Rating,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import { motion } from 'framer-motion';
import { ReviewContext } from '../ReviewContext';
import LoadingComponent from '../../utils/LoadingComponent';
import { showSwalNotification } from '../../utils/SwalResponse';

const AddReview = () => {
  const { loading, addNewReview } = useContext(ReviewContext);
  const { issueId } = useParams();

  const [formData, setFormData] = useState({
    issue_id: issueId,
    review: '',
    rating: null,
    is_anonymous: false, // Added is_anonymous field
  });

  const [errors, setErrors] = useState({
    review: '',
    rating: '',
  });

  // Load sessionStorage on mount
  useEffect(() => {
    const savedReview = sessionStorage.getItem('review');
    const savedRating = sessionStorage.getItem('rating');
    const savedAnonymous = sessionStorage.getItem('is_anonymous');

    setFormData((prev) => ({
      ...prev,
      review: savedReview || '',
      rating: savedRating !== null ? Number(savedRating) : null,
      is_anonymous: savedAnonymous === 'true', // Load saved anonymous preference
    }));
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    
    setFormData((prev) => ({ ...prev, [name]: newValue }));

    // Save to sessionStorage
    if (name === 'review') sessionStorage.setItem('review', value);
    if (name === 'is_anonymous') sessionStorage.setItem('is_anonymous', checked);

    // Validation
    setErrors((prev) => ({
      ...prev,
      [name]: value?.trim() === '' ? 'Review is required.' : '',
    }));
  };

  const handleRatingChange = (e, newVal) => {
    setFormData((prev) => ({ ...prev, rating: newVal }));
    sessionStorage.setItem('rating', newVal !== null ? newVal : '');

    setErrors((prev) => ({
      ...prev,
      rating: newVal === null ? 'Rating is required.' : '',
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = {
      review: formData.review.trim() === '' ? 'Review is required.' : '',
      rating: formData.rating === null ? 'Rating is required.' : '',
    };
    setErrors(newErrors);

    const hasError = Object.values(newErrors).some((err) => err !== '');
    if (hasError) return;

    const response = await addNewReview(formData);
    showSwalNotification(response);
    if (response?.status === 'success') {
      sessionStorage.removeItem('review');
      sessionStorage.removeItem('rating');
      sessionStorage.removeItem('is_anonymous');
      window.history.back();
    }
  };

  if (!!loading) return <LoadingComponent />;

  return (
    <motion.div
      initial={{ opacity: 0, x: -50 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 50 }}
      transition={{ duration: 0.5 }}
      className="d-flex justify-content-center align-items-center py-4 position-relative"
    >
    <button
      className="btn border border-danger position-absolute end-0 top-0 m-2"
      onClick={() => window.history.back()}
    >
      ❌
    </button>
      <Card sx={{ width: '100%', maxWidth: 600, p: 3, borderRadius: 4 }}>
        <CardContent>
          <Typography variant="h4" gutterBottom>
            Add Review
          </Typography>
          <TextField
            fullWidth
            label="Review"
            name="review"
            multiline
            rows={4}
            value={formData.review}
            onChange={handleChange}
            margin="normal"
            required
            error={!!errors.review}
            helperText={errors.review}
          />
          <Box className="my-2">
            <Typography variant="body2">Rating</Typography>
            <Rating
              sx={{ fontSize: '40px' }}
              name="rating"
              value={formData.rating}
              onChange={handleRatingChange}
            />
            {errors.rating && (
              <Typography color="error" fontSize="0.875rem">
                {errors.rating}
              </Typography>
            )}
          </Box>
          <FormControlLabel
            control={
              <Checkbox
                name="is_anonymous"
                checked={formData.is_anonymous}
                onChange={handleChange}
                color="primary"
              />
            }
            label="Post anonymously"
            sx={{ my: 1 }}
          />
          <Button
            fullWidth
            variant="contained"
            color="success"
            onClick={handleSubmit}
          >
            Submit Review
          </Button>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default AddReview;